import { useState } from 'react';
import type { Product } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, ApiError, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Table,
  Textarea,
  type Column,
} from '../components/ui';

const KIND_OPTIONS = [
  { value: 'service', label: '单次服务' },
  { value: 'package', label: '资源包' },
  { value: 'subscription', label: '按期服务' },
] as const;

const AUDIENCE_OPTIONS = [
  { value: 'all', label: '全部客户' },
  { value: 'personal', label: '仅个人' },
  { value: 'enterprise', label: '仅企业' },
] as const;

interface ProductForm {
  code: string;
  name: string;
  kind: 'service' | 'package' | 'subscription';
  priceYuan: string;
  termDays: string;
  entitlementCode: string;
  description: string;
  category: string;
  audience: 'all' | 'personal' | 'enterprise';
  features: string;
  sortOrder: string;
}

function emptyForm(): ProductForm {
  return {
    code: '',
    name: '',
    kind: 'service',
    priceYuan: '',
    termDays: '',
    entitlementCode: '',
    description: '',
    category: 'general',
    audience: 'all',
    features: '',
    sortOrder: '0',
  };
}

function toForm(product: Product): ProductForm {
  return {
    code: product.code,
    name: product.name,
    kind: product.kind,
    priceYuan: (product.priceMinor / 100).toFixed(2),
    termDays: product.termDays === null ? '' : String(product.termDays),
    entitlementCode: product.entitlementCode ?? '',
    description: product.description ?? '',
    category: product.category ?? 'general',
    audience: product.audience ?? 'all',
    features: (product.features ?? []).join('\n'),
    sortOrder: String(product.sortOrder ?? 0),
  };
}

export function ProductsPage() {
  const { can } = useAuth();
  const canManage = can('product.manage');
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const products = useAsync<{ products: Product[] }>(
    () => api.get('/api/products'),
    [version],
  );

  function openNew() {
    setEditing('new');
    setForm(emptyForm());
    setError(null);
    setFieldErrors({});
  }

  function openEdit(product: Product) {
    setEditing(product);
    setForm(toForm(product));
    setError(null);
    setFieldErrors({});
  }

  async function save() {
    const priceMinor = Math.round(Number(form.priceYuan) * 100);
    if (!Number.isFinite(priceMinor) || priceMinor <= 0) {
      setError('金额必须大于 0');
      return;
    }

    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const payload = {
        ...(editing === 'new' ? { code: form.code.trim() } : {}),
        name: form.name.trim(),
        kind: form.kind,
        priceMinor,
        termDays: form.termDays.trim() === '' ? null : Number(form.termDays),
        entitlementCode: form.entitlementCode.trim() || null,
        description: form.description.trim() || null,
        category: form.category.trim() || 'general',
        audience: form.audience,
        features: form.features
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== ''),
        sortOrder: Number(form.sortOrder) || 0,
      };

      if (editing === 'new') {
        await api.post('/api/products', payload);
      } else if (editing) {
        await api.patch(`/api/products/${editing.id}`, payload);
      }
      setEditing(null);
      setVersion((value) => value + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.message);
      } else {
        setError(describeError(caught));
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggle(product: Product) {
    try {
      await api.post(`/api/products/${product.id}/status`, {
        status: product.status === 'active' ? 'retired' : 'active',
      });
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  async function toggleSalesPause(product: Product) {
    try {
      await api.post(`/api/products/${product.id}/sales-pause`, {
        paused: !product.salesPaused,
      });
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  const columns: Column<Product>[] = [
    {
      key: 'name',
      header: '产品',
      render: (row) => (
        <div className="min-w-0">
          <p className="font-medium text-fg">{row.name}</p>
          <p className="font-mono text-xs text-fg-subtle">{row.code}</p>
        </div>
      ),
    },
    {
      key: 'price',
      header: '价格',
      render: (row) => <span className="tabular">{formatMoney(row.priceMinor, row.currency)}</span>,
    },
    {
      key: 'kind',
      header: '类型',
      render: (row) => KIND_OPTIONS.find((option) => option.value === row.kind)?.label ?? row.kind,
    },
    {
      key: 'audience',
      header: '受众',
      render: (row) =>
        AUDIENCE_OPTIONS.find((option) => option.value === row.audience)?.label ?? '全部客户',
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          <Badge tone={row.status === 'active' ? 'ok' : 'neutral'}>
            {row.status === 'active' ? '在售' : '已下架'}
          </Badge>
          {row.status === 'active' && row.salesPaused ? <Badge tone="warn">暂停接单</Badge> : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '260px',
      render: (row) =>
        canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => openEdit(row)}>
              编辑
            </Button>
            {row.status === 'active' ? (
              <Button size="sm" variant="ghost" onClick={() => void toggleSalesPause(row)}>
                {row.salesPaused ? '恢复接单' : '暂停接单'}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => void toggle(row)}>
              {row.status === 'active' ? '下架' : '上架'}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="产品"
        description="价格由这里决定，用户在控制台看到与支付的金额都来自它。下架不删除历史订单。"
        action={
          canManage ? (
            <Button variant="primary" onClick={openNew}>
              新建产品
            </Button>
          ) : null
        }
      />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="产品目录" hint={`${products.data?.products.length ?? 0} 个产品`} />
        <Table
          columns={columns}
          rows={products.data?.products ?? []}
          getKey={(row) => row.id}
          loading={products.loading}
          empty="还没有产品，先新建一个再开放购买。"
        />
      </Card>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? '新建产品' : '编辑产品'}
        description="code 是稳定标识，创建后不可修改：历史订单与通道映射都靠它。"
        onClose={() => setEditing(null)}
        width="max-w-xl"
        footer={
          <>
            <Button onClick={() => setEditing(null)}>取消</Button>
            <Button variant="primary" loading={saving} onClick={() => void save()}>
              保存
            </Button>
          </>
        }
      >
        {error ? (
          <div className="mb-3">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}
        <div className="space-y-3">
          <Field label="产品编码" htmlFor="code" error={fieldErrors.code}>
            <Input
              id="code"
              value={form.code}
              onChange={(value) => setForm({ ...form, code: value })}
              placeholder="fleet-analysis"
              maxLength={63}
            />
          </Field>
          <Field label="产品名称" htmlFor="name" error={fieldErrors.name}>
            <Input
              id="name"
              value={form.name}
              onChange={(value) => setForm({ ...form, name: value })}
              maxLength={60}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="类型" htmlFor="kind">
              <Select
                id="kind"
                value={form.kind}
                onChange={(value) => setForm({ ...form, kind: value })}
                options={KIND_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              />
            </Field>
            <Field label="价格（元）" htmlFor="price" error={fieldErrors.priceMinor}>
              <Input
                id="price"
                type="number"
                value={form.priceYuan}
                onChange={(value) => setForm({ ...form, priceYuan: value })}
                placeholder="49.00"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="有效期（天）" htmlFor="term" hint="留空表示一次性服务">
              <Input
                id="term"
                type="number"
                value={form.termDays}
                onChange={(value) => setForm({ ...form, termDays: value })}
                placeholder="365"
              />
            </Field>
            <Field label="权益编码" htmlFor="entitlement" hint="留空表示不发放权益">
              <Input
                id="entitlement"
                value={form.entitlementCode}
                onChange={(value) => setForm({ ...form, entitlementCode: value })}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="分类" htmlFor="category">
              <Input
                id="category"
                value={form.category}
                onChange={(value) => setForm({ ...form, category: value })}
                placeholder="general"
              />
            </Field>
            <Field label="受众" htmlFor="audience">
              <Select
                id="audience"
                value={form.audience}
                onChange={(value) => setForm({ ...form, audience: value })}
                options={AUDIENCE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              />
            </Field>
          </div>
          <Field label="简介" htmlFor="description">
            <Textarea
              id="description"
              value={form.description}
              onChange={(value) => setForm({ ...form, description: value })}
              rows={2}
              maxLength={500}
            />
          </Field>
          <Field label="卖点" htmlFor="features" hint="每行一条，最多 20 条">
            <Textarea
              id="features"
              value={form.features}
              onChange={(value) => setForm({ ...form, features: value })}
              rows={3}
            />
          </Field>
          <Field label="排序" htmlFor="sort" hint="数字小的排在前面">
            <Input
              id="sort"
              type="number"
              value={form.sortOrder}
              onChange={(value) => setForm({ ...form, sortOrder: value })}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
